import { Bridge } from 'arb-ts';
import { ethers } from 'ethers';
import { range } from 'lodash';

import { LootPortalAbi, DeevyAbi } from '@contracts';
import { cashedSupplyData } from '@data';
import { getContract } from '@frameworks/ethers';
import { toBN, parseBase64, fromBase64ToAscii, timeout } from '@utils';
import {
  DeevyService,
  WarpProps,
  GetCollectionProps,
  TransactionResponse,
  Web3Provider,
  TransactionService,
  DeevyNft,
  SupplyData,
  Config,
} from '@types';

export class DeevyServiceImpl implements DeevyService {
  private web3Provider: Web3Provider;
  private transactionService: TransactionService;
  private config: Config;

  constructor({
    web3Provider,
    transactionService,
    config,
  }: {
    web3Provider: Web3Provider;
    transactionService: TransactionService;
    config: Config;
  }) {
    this.web3Provider = web3Provider;
    this.transactionService = transactionService;
    this.config = config;
  }

  public async warp(props: WarpProps): Promise<TransactionResponse> {
    const { lootId, walletAddress } = props;
    const { CONTRACT_ADDRESSES } = this.config;

    const l1Provider = this.web3Provider.getInstanceOf('ethereum');
    const l1Signer = l1Provider.getSigner(walletAddress);
    const l2Provider = this.web3Provider.getInstanceOf('arbitrum');
    const l2Signer = l2Provider.getSigner(walletAddress);

    const bridge = await Bridge.init(l1Signer, l2Signer);

    /**
     * Base submission cost is a special cost for creating a retryable ticket; querying the cost requires us to know how many bytes of calldata out retryable ticket will require, so let's figure that out.
     * We'll get the bytes for our greeting data, then add 4 for the 4-byte function signature.
     */
    const encodedData = ethers.utils.defaultAbiCoder.encode(
      ['address', 'uint256'],
      [walletAddress, lootId]
    );
    const encodedDataLength = ethers.utils.hexDataLength(encodedData) + 4; // 4 bytes func identifier

    /**
     * Now we can query the submission price using a helper method; the first value returned tells us the best cost of our transaction; that's what we'll be using.
     * The second value (nextUpdateTimestamp) tells us when the base cost will next update (base cost changes over time with chain congestion; the value updates every 24 hours). We won't actually use it here, but generally it's useful info to have.
     */
    const [submissionPriceWei, nextUpdateTimestamp] =
      await bridge.l2Bridge.getTxnSubmissionPrice(encodedDataLength);
    console.log(
      `Retryable base submission price: ${submissionPriceWei.toString()}`
    );

    const timeNow = Math.floor(new Date().getTime() / 1000);
    console.log(
      `Time till price update: ${
        nextUpdateTimestamp.toNumber() - timeNow
      } secs.`
    );

    /**
     * ...Okay, but on the off chance we end up underpaying, our retryable ticket simply fails.
     * This is highly unlikely, but just to be safe, let's increase the amount we'll be paying (the difference between the actual cost and the amount we pay gets refunded to our address on L2 anyway)
     * (Note that in future releases, the a max cost increase per 24 hour window of 150% will be enforced, so this will be less of a concern.)
     */
    const submissionPriceWeiMul5 = submissionPriceWei.mul(5);
    /**
     * Now we'll figure out the gas we need to send for L2 execution; this requires the L2 gas price and gas limit for our L2 transaction
     *
     * For the L2 gas price, we simply query it from the L2 provider, as we would when using L1
     */
    const gasPriceBid = await bridge.l2Provider.getGasPrice();
    console.log(`L2 gas price: ${gasPriceBid.toString()}`);

    /**
     * For the gas limit, we'll simply use a hard-coded value (for more precise / dynamic estimates, see the estimateRetryableTicket method in the NodeInterface L2 "precompile")
     */
    const maxGas = 2600000;
    /**
     * With these three values, we can calculate the total callvalue we'll need our L1 transaction to send to L2
     */
    const callValue = submissionPriceWeiMul5.add(gasPriceBid.mul(maxGas));

    console.log(
      `Sending to L2 with ${callValue.toString()} callValue for L2 fees:`
    );
    console.log(
      `yarn warp-loot:eth_rinkeby --sender-index 0 --max-submission-cost ${submissionPriceWeiMul5} --max-gas ${maxGas} --gas-price-bid ${gasPriceBid} --msg-value ${callValue} --loot-id ${lootId} --send-tx false`
    );

    const lootPortalContract = getContract(
      CONTRACT_ADDRESSES.LOOT_PORTAL,
      LootPortalAbi,
      this.web3Provider.getSigner()
    );

    // const estimateGas = await lootPortalContract.estimateGas.warpLoot(
    //   lootId.toString(),
    //   submissionPriceWeiMul5.toString(),
    //   maxGas.toString(),
    //   gasPriceBid.toString(),
    //   {
    //     gasLimit: '2100000000000000',
    //     gasPrice: '90000000000',
    //     value: callValue.toString(),
    //   }
    // );

    // console.log({ estimateGas: estimateGas.toString() }); // 146873
    const gasLimit = '165000'; // ~15% more than estimated

    const tx = await this.transactionService.execute({
      fn: lootPortalContract.warpLoot,
      args: [
        lootId.toString(),
        submissionPriceWeiMul5.toString(),
        maxGas.toString(),
        gasPriceBid.toString(),
      ],
      overrides: { gasLimit, value: callValue.toString() },
    });

    return tx;
  }

  public async getCollection(props: GetCollectionProps): Promise<DeevyNft[]> {
    const { walletAddress } = props;
    const { CONTRACT_ADDRESSES } = this.config;

    const l2Provider = this.web3Provider.getInstanceOf('arbitrum');
    const deevyContract = getContract(
      CONTRACT_ADDRESSES.DEEVY,
      DeevyAbi,
      l2Provider
    );
    const balance = await deevyContract.balanceOf(walletAddress);
    const collectionPromise = Array.from(
      Array(toBN(balance).toNumber()).keys()
    ).map(async (index) => {
      const tokenId = await deevyContract.tokenOfOwnerByIndex(
        walletAddress,
        index.toString()
      );
      const tokenUriBase64 = await deevyContract.tokenURI(tokenId.toString());
      const tokenUri = parseBase64(tokenUriBase64);
      const image = fromBase64ToAscii(tokenUri.image);
      return { tokenId: tokenId.toString(), tokenUri, image };
    });

    return Promise.all(collectionPromise);
  }

  public async getSupplyData(): Promise<SupplyData> {
    const DEEVY_SUPPLY = 11111;
    const { CONTRACT_ADDRESSES } = this.config;

    const l2Provider = this.web3Provider.getInstanceOf('arbitrum');
    const deevyContract = getContract(
      CONTRACT_ADDRESSES.DEEVY,
      DeevyAbi,
      l2Provider
    );

    const supplyIds = range(0, DEEVY_SUPPLY + 1);
    const totalSupply = await deevyContract.totalSupply();
    const mintedIdsPromise = range(
      cashedSupplyData.mintedIds.length - 1,
      totalSupply
    ).map(async (index, i) => {
      await timeout(i);
      const tokenId = await deevyContract.tokenByIndex(index.toString());
      return toBN(tokenId).toNumber();
    });

    const newMintedIds: number[] = await Promise.all(mintedIdsPromise);
    const cashedMintedIds: number[] = cashedSupplyData.mintedIds;
    const mintedIds = [...cashedMintedIds, ...newMintedIds];
    const availableIds: number[] = supplyIds.filter(
      (tokenId) => !mintedIds.includes(tokenId)
    );

    console.log({ supplyIds, mintedIds, availableIds });

    return {
      supply: supplyIds,
      minted: mintedIds,
      available: availableIds,
    };
  }
}
